/**
 * Card wrapper para interesses do lead
 * Renderiza PropertyCard (properties) ou GenericDocumentCard (outros)
 * + botões aprovar/remover no bottom-right da imagem
 * + badge de status no bottom-left da imagem
 */

import React from 'react';
import type { DatabaseDocumentResponse } from '@troia-v3/types';
import { PropertyCard } from '@/modules/databases/components/content/PropertyCard';
import { GenericDocumentCard } from '@/modules/databases/components/content/GenericDocumentCard';
import { Icon } from '@/shared/icons'
export interface InterestCardProps {
  document: DatabaseDocumentResponse<any>;
  interestStatus: 'pending' | 'approved';
  onRemove: () => void;
  onApprove?: () => void;
  isRemoving: boolean;
  isApproving?: boolean;
}

export const InterestCard: React.FC<InterestCardProps> = ({
  document,
  interestStatus,
  onRemove,
  onApprove,
  isRemoving,
  isApproving
}) => {
  return (
    <div className="relative group">
      {/* Card de documento */}
      {document.type === 'properties' ? (
        <PropertyCard property={document} alwaysLight />
      ) : (
        <GenericDocumentCard document={document} alwaysLight />
      )}

      {/* Overlay sobre a área da imagem (aspect-[4/3]) para posicionar badges e botões */}
      <div className="absolute top-0 left-0 right-0 aspect-[4/3] pointer-events-none">
        {/* Status Badge - Bottom Left */}
        <div
          className="absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-medium shadow-sm pointer-events-auto"
          style={interestStatus === 'approved'
            ? { backgroundColor: '#DCFCE7', color: '#166534' }
            : { backgroundColor: '#FEF3C7', color: '#92400E' }
          }
        >
          {interestStatus === 'approved' ? 'Aprovado' : 'Sugerido'}
        </div>

        {/* Action Buttons - Bottom Right */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all pointer-events-auto">
          {/* Botão Aprovar (somente se pending) */}
          {interestStatus === 'pending' && onApprove && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onApprove();
              }}
              disabled={isApproving}
              className="p-1.5 rounded-lg transition-all shadow-sm"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#16A34A',
                border: '1px solid #BBF7D0'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#DCFCE7';
                e.currentTarget.style.borderColor = '#86EFAC';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.borderColor = '#BBF7D0';
              }}
              title="Aprovar interesse"
            >
              {isApproving ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="check" className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Botão Remover */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            disabled={isRemoving}
            className="p-1.5 rounded-lg transition-all shadow-sm"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#64748b',
              border: '1px solid #e5e7eb'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#FEE2E2';
              e.currentTarget.style.color = '#DC2626';
              e.currentTarget.style.borderColor = '#FECACA';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.color = '#64748b';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
            title="Remover interesse"
          >
            {isRemoving ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="trash" className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
